/**
 * How the console words what the API decided about reaching a learner, and about the funnel.
 *
 * **No React, no DOM, no import that reaches either.** The browser package's tests are plain
 * Node with `lib: ["ES2022"]` and nothing else — see `tsconfig.test.json` — so anything worth
 * asserting has to live in a module that a Node process can import. That is not a testing
 * convenience here: the cell this file words is *"we cannot reach this person at all"*, which
 * is the single most consequential thing on the screen, and a label that says "email" when the
 * answer is "nothing" would hide a learner nabu is silently failing to teach. A component
 * would have needed jsdom to assert, so the wording is not in a component.
 *
 * The *decisions* are all on the server (`reachView` in `packages/api/src/admin.ts`). Nothing
 * here re-derives whether somebody is reachable; it turns an answer into words and a severity,
 * which is the division the rest of the console keeps — see `AdminNode.active` in `api.ts` for
 * the same rule stated about curriculum nodes.
 */

/**
 * The shapes `/api/admin/learners` answers with, declared here rather than in `api.ts`.
 *
 * The dependency runs this way round — `api.ts` re-exports these — because `api.ts` reaches
 * `fetch` and `../api`, and a `import type` from a module that does would still make the test
 * compiler resolve it. A DOM-free module that imports a DOM-bound one is DOM-free only until
 * somebody adds a value import to it, which is the kind of breakage that shows up as a test
 * suite that stopped compiling for reasons nobody in this file caused.
 */

/** What a question sent right now would travel on. Mirrors `ReachChannel` in the API. */
export type ReachChannel = 'push' | 'email' | 'none';

/** Why mail is or is not an answer. Mirrors `EmailReach` in the API. */
export type EmailReach = 'none' | 'unproved' | 'suppressed' | 'opted_out' | 'ready';

export interface AdminReach {
  devices: number;
  /** An Apple push endpoint **is** an installed iOS app — an iOS tab has no `PushManager`. */
  iosDevices: number;
  otherDevices: number;
  email: EmailReach;
  via: ReachChannel;
  unreachable: boolean;
}

export interface AdminAnswering {
  sent: number;
  answered: number;
  correct: number;
  lastAnsweredDay: string | null;
  /** How many of the learner's own days the two sums cover. Stated, never implied. */
  windowDays: number;
}

/**
 * How loudly a cell should read.
 *
 * Three levels rather than a boolean because the middle one carries real information: a
 * learner reachable only by email is being taught, and is also one unsubscribe away from not
 * being. Painting that the same as a working push would lose the warning; painting it the same
 * as unreachable would cry wolf about somebody who is fine.
 */
export type ReachTone = 'ok' | 'warn' | 'bad';

export interface ReachLabel {
  /** The short phrase in the cell. */
  text: string;
  /** The line under it, saying what the phrase rests on. */
  detail: string;
  tone: ReachTone;
}

/** Why mail is not an answer, in the words an operator can act on. */
const EMAIL_REASON: Record<EmailReach, string> = {
  none: 'no address on the account',
  // The distinction that matters most and reads as a technicality: we hold the address and
  // the mailer will not use it, because the `EMAIL#` pointer — the thing that proves the
  // address belongs to this account — does not name them. A screen that said "has email"
  // would be describing something no code will act on.
  unproved: 'address never proved',
  suppressed: 'address bounced or complained',
  opted_out: 'unsubscribed',
  ready: 'address proved and mailable',
};

/**
 * What this learner actually gets, as a phrase.
 *
 * `unreachable` is deliberately the blunt word and not "no channel" or "not configured".
 * Since F-103 push is how nabu reaches anybody and email is the fallback beneath it; a learner
 * with neither has questions composed for them, items claimed, reviews scheduled, and is
 * taught nothing — and from every other column on this screen that is indistinguishable from
 * somebody who is simply quiet. The word has to be the one that stops a reader.
 */
export function reachLabel(reach: AdminReach | undefined): ReachLabel {
  if (!reach) return { text: '—', detail: 'not reported by this API', tone: 'warn' };

  if (reach.via === 'none') {
    return {
      text: 'UNREACHABLE',
      detail:
        reach.devices === 0
          ? `no device · ${EMAIL_REASON[reach.email]}`
          : // Cannot happen while `reachView` puts push first, and is worded rather than
            // omitted because a future change to that order must not produce a blank cell.
            `${reach.devices} device(s) · ${EMAIL_REASON[reach.email]}`,
      tone: 'bad',
    };
  }

  if (reach.via === 'email') {
    return {
      text: 'email only',
      detail: 'no push device · nudges by mail, and an unsubscribe ends them',
      tone: 'warn',
    };
  }

  const platforms =
    reach.iosDevices > 0 && reach.otherDevices > 0
      ? `${reach.iosDevices} iOS · ${reach.otherDevices} other`
      : reach.iosDevices > 0
        ? `${reach.iosDevices} iOS (installed app)`
        : `${reach.otherDevices} other`;

  return {
    text: `push · ${reach.devices} device${reach.devices === 1 ? '' : 's'}`,
    detail: platforms,
    tone: 'ok',
  };
}

/**
 * Whether the email half is worth flagging even though push is working.
 *
 * A learner on push whose address has hard-bounced has no fallback left: the day their
 * browser rotates its endpoint or they clear site data, they become unreachable with nothing
 * to catch them. It is not urgent and it is not nothing, which is exactly the class of fact a
 * dense table is good at carrying and an alert is not.
 */
export function emailNote(reach: AdminReach | undefined): string | null {
  if (!reach || reach.via !== 'push') return null;
  if (reach.email === 'ready') return null;
  return `fallback unavailable: ${EMAIL_REASON[reach.email]}`;
}

/** The channel column's sort weight: the unreachable first, because they are the work. */
export function reachRank(via: ReachChannel | undefined): number {
  return via === 'none' ? 0 : via === 'email' ? 1 : 2;
}

// ---- Whether they are answering ----

export interface AnsweringLabel {
  text: string;
  detail: string;
  tone: ReachTone;
}

/**
 * Answered over sent, and what it is safe to conclude from it.
 *
 * **Nothing sent is not a low answer rate**, and collapsing the two is the mistake this
 * function exists to not make: a learner with no questions sent has an undefined ratio, not a
 * zero, and showing `0%` would put somebody nabu has never asked anything in the same column
 * as somebody ignoring it. The first is a fault in the sweep or an objective that never
 * started; the second is a learner going quiet. They need opposite responses.
 */
export function answeringLabel(answering: AdminAnswering | undefined): AnsweringLabel {
  if (!answering) return { text: '—', detail: 'not reported by this API', tone: 'warn' };
  if (answering.sent === 0 && answering.answered === 0) {
    return { text: 'nothing sent', detail: 'no questions in the window', tone: 'warn' };
  }

  const rate = answering.sent > 0 ? Math.round((answering.answered / answering.sent) * 100) : null;
  const accuracy =
    answering.answered > 0 ? Math.round((answering.correct / answering.answered) * 100) : null;

  return {
    text: `${answering.answered}/${answering.sent}${rate === null ? '' : ` · ${rate}%`}`,
    detail:
      accuracy === null
        ? `${answering.windowDays} active day(s)`
        : `${accuracy}% right · ${answering.windowDays} active day(s)`,
    // A learner answering less than a third of what is sent is the "stuck" this screen exists
    // to find. Below a tenth reads as gone rather than struggling.
    tone: rate === null ? 'warn' : rate < 10 ? 'bad' : rate < 34 ? 'warn' : 'ok',
  };
}

// ---- The install funnel ----

/**
 * The one ratio the funnel exists to produce, and the honest way to say it.
 *
 * `installed:ios` over `offered:ios-safari + offered:ios-other`. **It is a ratio of two
 * populations and not a path traced through one**, because a home-screen app on iOS gets its
 * own storage jar and cannot recognise itself as the browser that was offered the card — so no
 * install is ever joined to the offer that produced it. That caveat travels with the number
 * wherever it is shown; it was the top half of the CloudWatch dashboard F-125 deleted, and a
 * funnel that overstates its certainty is worse than none when the decision resting on it is
 * whether to build a native app.
 *
 * `null` rather than zero when nothing was offered. A denominator of zero is not a rate of
 * nought, and "0% of iOS visitors install it" is the most expensive wrong sentence this
 * feature can put on a screen.
 */
export function iosInstallRate(totals: Record<string, number> | undefined): number | null {
  if (!totals) return null;
  const offered = (totals['offered:ios-safari'] ?? 0) + (totals['offered:ios-other'] ?? 0);
  if (offered === 0) return null;
  return (totals['installed:ios'] ?? 0) / offered;
}

/** The same for everything that is not Apple, where the offer and the install share a browser. */
export function promptInstallRate(totals: Record<string, number> | undefined): number | null {
  if (!totals) return null;
  const offered = totals['offered:prompt'] ?? 0;
  if (offered === 0) return null;
  return (totals['installed:other'] ?? 0) / offered;
}

/** A rate as a percentage, or the em dash that means "no denominator". */
export function percent(rate: number | null): string {
  return rate === null ? '—' : `${Math.round(rate * 100)}%`;
}
